<template>
  <div>
    <div class="mb-2 text-sm">
      {{ $t.passwordMustContain }}:
    </div>
    <ul :aria-hidden="!!id" class="space-y-1">
      <li v-for="({ invalid, label }, key) in validationRules" :key>
        <vf-form-validation :invalid>
          {{ label }}
        </vf-form-validation>
      </li>
    </ul>
    <span v-if="id" :id class="sr-only">{{ validationRulesList }}</span>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  password: string
}>()

const { $t } = useNuxtApp()
const id = useAttrs().id as string

const validationRules = computed(() => [
  { invalid: validatePasswordRequirement.length(props.password), label: $t.eightOrMoreCharacters },
  { invalid: validatePasswordRequirement.upper(props.password), label: $t.oneUppercaseLetter },
  { invalid: validatePasswordRequirement.lower(props.password), label: $t.oneLowercaseLetter },
  { invalid: validatePasswordRequirement.number(props.password), label: $t.oneNumber },
])

/**
 * The support for aria-describedby and aria-details on Safari can be broken in some cases;
 * the former can be skipped when it's pointing to an element with complex content (i.e. the list we have here)
 * and the latter is not supported and doesn't work at all.
 *
 * In order to make the password rules at least somewhat accessible, we're compiling a list of their labels
 * and displaying them in an sr-only element as a simple string, so even Safari can handle reading it out.
 * In order to avoid duplications on real browsers, the list itself becomes aria-hidden.
 *
 * The trigger for this is passing an id attribute to the component.
 * The assumption is that it will be included in another element's aria-labeledby attribute.
 */
const validationRulesList = validationRules.value.map(({ label }) => label).join(', ')
</script>
